import type { ChangeEvent } from 'react'
import { kycStatusMap } from '../utils/message'
import { SearchParams, TransactionSearchParams } from './propsTypes'
import { PortableTextBlock } from '@portabletext/react'
import { Role } from './userTypes'
import { FileUploadPurpose } from './file-upload'

export type StepperStatus = Record<string, string | undefined>

export type AssetsClass =
  | 'Trade Finance'
  | 'Fixed Income'
  | 'Private Equity'
  | 'Private Credit'
  | 'Mutual Fund'
  | 'Listed Equity'
export type PublishingStatus = 'PUBLISHED' | 'UNPUBLISHED'
export type AccountStatus = 'ACTIVE' | 'INACTIVE' | 'DORMANT'
export type AssistanceCategories =
  | 'KYC Verification'
  | 'Investment Limit'
  | 'Investment Opportunities'
  | 'Account Management'
  | 'Business Account Setup'
  | 'Withdrawals & Refunds'
  | 'Feedback & Complaints'
  | 'Others'

export type DocumentStatus = 'CREATED' | 'OPENED' | 'COMPLETED'
export type DocumentType = 'INVESTMENT_AGREEMENT' | 'SELF_DECLARATION' | 'TERMSHEET_AGREEMENT'

export const InvestmentStatus_v1 = {
  INITIATED: 'INITIATED',
  PAYMENT_DONE: 'PAYMENT_DONE',
  ORDER_PLACED: 'ORDER_PLACED',
  REINVESTMENT_PENDING: 'REINVESTMENT_PENDING',
  REJECTED: 'REJECTED',
  TRADE_EXECUTED: 'TRADE_EXECUTED',
  CANCELLED: 'CANCELLED',
  REFUND_PROCESSED: 'REFUND_PROCESSED',
}

export type InvestmentStatusType_v1 = (typeof InvestmentStatus_v1)[keyof typeof InvestmentStatus_v1]

export const WithdrawalStatus = {
  INITIATED: 'INITIATED',
  ORDER_PLACED: 'ORDER_PLACED',
  REJECTED: 'REJECTED',
  TRADE_EXECUTED: 'TRADE_EXECUTED',
  CANCELLED: 'CANCELLED',
}

export type WithdrawalStatusType = (typeof WithdrawalStatus)[keyof typeof WithdrawalStatus]

export type SubjectCategories = 'Onboard' | 'General' | 'Inquiry'

export interface InvestorContactUsFormProps_v1 {
  name: string
  email: string
  phone: string
  assistance_category: AssistanceCategories
  subject: string
  message: string
  attachments: File[]
}

export interface GuestContactUsFormProps_v1 {
  firstName: string
  lastName: string
  email: string
  subject: AssistanceCategories
  message: string
}

export interface BusinessContactData {
  firstName: string
  lastName: string
  company: string
  email: string
  message: string
}

export interface InvestorFeedbackFormProps {
  description: string
}

// Dashboard
export interface DashboardSummary {
  totalInvestedAmount: number
  totalReturnsTillDate: number
  realizedGains: number
  unrealizedGains: number
  realizedGainsAmount: number
  unrealizedGainsAmount: number
  totalUnits?: number
  currentNav?: number
  /** Formatted ownership % for non-fund holdings, e.g. "0.14%". Null for funds / N/A. */
  underlyingExposure?: string | null
  /** Valuation narrative shown under underlying exposure. */
  underlyingExposureDescription?: string | null
  /** Raw ownership percentage for non-fund holdings. */
  underlyingExposurePercent?: number | null
  investmentDistributions?: {
    asset_class: AssetsClass
    ratio: number
  }[]
}

export interface DashboardInvestmentDistribution {
  [key: string]: number
}

export interface InvestmentDetailsProps {
  dashboardSummary: DashboardSummary
  investmentsData: DashboardInvestmentsGraphs
  distributionData: DashboardInvestmentDistribution | undefined
  isDashboardInvestmentView: boolean | undefined
  totalInvestments?: number
  opportunity?: OpportunityDetails
  maxWithdrawalAmount?: string
  isLockInPeriodActive?: boolean
  endDateForLockIn?: string
  activeWithdrawalRequest: Withdrawal
  userInvestmentData?: Investment | null
  selectedFilter?: { text: string; value: string }
  onFilterChange?: (value: string) => void
}

export interface DashboardActiveInvestment {
  currentInvestmentValue: number
  investments: DashboardInvestmentGraph[]
}
export interface DashboardInvestmentGraph {
  asset_class: string
  cover_image: string
  investment_return: string
  opportunity_id: number
  opportunity_title: string
  short_opportunity_summary: string
  values: DashboardInvestmentGraphValues[]
}

export interface DashboardInvestmentGraphValues {
  date: string
  currentInvestmentValue: number
}

export interface DashboardInvestmentsSingleGraph extends DashboardInvestmentGraphValues {
  opportunity_id: number
  opportunity_title: string
}
export interface DashboardInvestmentsGraphs {
  investments: Array<DashboardInvestmentsSingleGraph[]>
}

export interface DashboardHoldings {
  asset_class: string
  current_price: number
  invested_amount: number
  opportunity_id: number
  opportunity_title: string
  total_return: number
  total_gains: number
  total_units: number
  current_nav: number
  /** Formatted ownership % for non-fund holdings, e.g. "0.14%". Null for funds / N/A. */
  underlyingExposure?: string | null
  /** Valuation narrative shown under underlying exposure. */
  underlyingExposureDescription?: string | null
  /** Raw ownership percentage for non-fund holdings. */
  underlyingExposurePercent?: number | null
}

export interface TransactionTermsheet {
  status?: string
  documentId?: string
  downloadLink?: string
  signed?: boolean
  ready?: boolean
  stale?: boolean
}

export interface TransactionAllotmentConfirmation {
  key?: string
  downloadLink?: string
}

export interface TransactionPaymentCopy {
  key?: string
  downloadLink?: string
}

export interface DashboardInvestmentTransactionsRaw {
  date: string
  txn_type: string
  amount: number
  quantity?: number | string | null
  unit_price?: number | string | null
  order_status: string
  portfolio_id: string
  order_id: string
  termsheet?: TransactionTermsheet | null
  allotmentConfirmation?: TransactionAllotmentConfirmation | null
  paymentCopy?: TransactionPaymentCopy | null
  actual_amount_received?: number | null
  actual_invested_amount?: number | null
  actual_amount_sent?: number | null
  /** Share-priced orders (CB-239): whole shares and price per share in cents. */
  shares?: number | null
  price_per_share?: number | null
}

export interface DashboardInvestmentTransactions {
  opportunity_title: string
  asset_class: string
  transaction_date: string
  transaction_type: string
  amount: number
  quantity?: number | null
  order_status: string
  portfolio_id: string
  order_id: string
  termsheet?: TransactionTermsheet | null
  allotmentConfirmation?: TransactionAllotmentConfirmation | null
  paymentCopy?: TransactionPaymentCopy | null
  actual_amount_received?: number | null
  actual_invested_amount?: number | null
  actual_amount_sent?: number | null
  /** Share-priced orders (CB-239): whole shares and price per share in cents. */
  shares?: number | null
  price_per_share?: number | null
}

export interface DashboardPresenterProps {
  name: string
  email: string
  kycStatus?: string
  dashboardSummary: DashboardSummary
  investmentDistribution?: DashboardInvestmentDistribution
  investmentsData: DashboardInvestmentsGraphs
  holdingsData?: DashboardHoldings[]
  transactionsData?: DashboardInvestmentTransactions[]
  opportunity?: OpportunityDetails
  inProgressInvestments: Array<Investment & RawOpportunity & UserDetails>
  activeWithdrawalRequests?: Array<Withdrawal>
  userInvestmentDetail?: UserInvestmentDetails
  currentPage: number
  pagination: {
    total: number
    limit: number
    count: number
    offset: number
  }
  maxWithdrawalAmount?: string
  isLockInPeriodActive?: boolean
  endDateForLockIn?: string
  handleSearchParams?: (...args: any) => void
  handleCurrentPage: (page: number) => void
  isDashboardInvestmentView?: boolean
  portfolioIds?: Array<{ value: string; text: string }>
  searchParams?: TransactionSearchParams
  userInvestmentData?: Investment | null
  selectedFilter?: { text: string; value: string }
  onFilterChange?: (value: string) => void
  onRefreshDashboard?: () => void
}

export interface WithdrawalDetailsPopupProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  withdrawalDetails: {
    withdrawalApprovalDate?: string
    amountReceivableDate?: string
    grossMoneyToBeReceived?: number
    charges?: {
      exitLoad: number
      profitSharing: number
      hostingFee: number
    }
    netMoneyToBeReceived?: number
    withdrawalAmount?: number
    effectiveReturn?: number
  } | null
  isLoading: boolean
  portfolioIds?: Array<{ value: string; text: string }>
  searchParams?: TransactionSearchParams
  error?: string
}

// user

export interface UserDetails {
  created_at: string
  updated_at: string
  email: string
  name: string
  is_verified: boolean
  account_status: AccountStatus
  investment_limit: string
  total_investment: string
  role: string
  crm_contact_id: string
  user_source: string
  investor_reference: string
  phone: string | null
  country: string | null
  address: string | null
}

export interface UserProfile extends UserDetails {
  whatsapp_consent: boolean
  phone_preference: string
  phone_skip_date: string | null
}

export interface UserLogin {
  name: string
  userId: number
  email: string
  authToken: string
  role: Role.investor
  account_status: AccountStatus
  is_verified: boolean
  acceptedNDA?: boolean
  mustSetPassword?: boolean
}

// opportunity

export interface OpportunityTerm {
  level1:
    | {
        header: string
        termObjects: Array<{ title: string; content: string }>
      }
    | undefined
  level2:
    | {
        header: string
        termObjects: Array<{ title: string; content: string }>
      }
    | undefined
}

export interface RawOpportunity {
  id: number
  created_at: string
  updated_at: string
  opportunity_title: string
  opportunity_slug: string
  opportunity_summary: string
  is_featured: boolean
  asset_class: AssetsClass
  investment_return: string
  jurisdiction: string
  ticket_size: string
  closing_date: string
  currency_code: string
  raise_goal: string
  status: string
  video: string
  images: string[]
  documents: {
    url: string
    title: string
  }[]
  key_factors: string[]
  highlights: string[]
  terms: OpportunityTerm
  metadata?: {
    company: {
      about: string
      website?: string
      key_performance?: string
      name?: string
    }
    termsheet_agreement_template_url?: string
    /** When true, opportunity detail shows Request Allocation instead of invest CTA */
    request_allocation_enabled?: boolean
    /** When true, the opportunities page spotlights this opportunity (hero + highlighted card). */
    highlight_enabled?: boolean
    /** Shown on the investment detail card when set. */
    securities_offering?: string
    /** Shown on the investment detail card when set. */
    underlying_fund?: string
    /** Price per share in USD; when set the invest flow uses the share calculator. */
    price_per_share?: string
    /** Upfront / sales fee percent applied to the share investment amount. */
    sales_fee_percent?: string
  }
  cover_image: string
  short_opportunity_summary: string
  coupon_payment_interval_in_months: number | null
  access_token?: string | null
  no_of_investors: number
  total_investment_amount: string
  asset_class_data?: {
    investment_period_in_days?: number
    interest_charged_by_credibila_to_opportunity_percent?: number
    expected_spread_percent?: number
    days_from_withdraw_request_to_payment?: number
    exit_load_percent?: number
    profit_sharing_to_credibila_percent?: number
    hosting_fee_percent?: number
    has_lock_in_period?: boolean
    lock_in_period_in_days?: number
    management_fee_percent?: number
    total_shares?: number // For Listed Equity
  }
  updates: {
    date: string
    title: string
    description: string
  }[]
  minimum_return?: string
  maximum_return?: string
  transfer_page_enabled?: boolean
}

export type OpportunityCardProps = RawOpportunity

export interface OpportunityDetails {
  id: RawOpportunity['id']
  closingDate: RawOpportunity['closing_date']
  projectName: RawOpportunity['opportunity_title']
  projectSlug: RawOpportunity['opportunity_slug']
  irr: RawOpportunity['investment_return']
  ticketSize: RawOpportunity['ticket_size']
  projectSummary: RawOpportunity['opportunity_summary']
  companyWebsite: string
  location: RawOpportunity['jurisdiction']
  raiseGoal: RawOpportunity['raise_goal']
  companyDetails: string
  keyPerformance: string
  projectCover: RawOpportunity['cover_image'] | string
  assetClass: RawOpportunity['asset_class']
  isFeatured: RawOpportunity['is_featured']
  companyName: string
  isPublished: boolean
  currencyCode?: RawOpportunity['currency_code']
  productImages: RawOpportunity['images']
  committedAmount: RawOpportunity['total_investment_amount']
  noOfInvestors: RawOpportunity['no_of_investors']
  terms: RawOpportunity['terms']
  documents: RawOpportunity['documents']
  updates: RawOpportunity['updates']
  highlights: RawOpportunity['highlights']
  video: RawOpportunity['video']
  coverImage: RawOpportunity['cover_image']
  keyFactors: RawOpportunity['key_factors']
  assetClassData?: RawOpportunity['asset_class_data']
  metadata?: RawOpportunity['metadata']
  isHideCompanyDetails?: boolean
  isHighCommitenceProject?: boolean
  minimumReturn?: RawOpportunity['minimum_return']
  maximumReturn?: RawOpportunity['maximum_return']
  couponPaymentIntervalInMonths?: RawOpportunity['coupon_payment_interval_in_months']
  transferPageEnabled?: RawOpportunity['transfer_page_enabled']
}

export interface FeaturedProjectProps_v1 {
  featuredProject: OpportunityCardProps
  handleClick: (projectSlug: string, id: number, title?: string) => void
  handleSaveForLater: (id: number) => void
}

export interface OpportunityListPresenterProps {
  projects: RawOpportunity[]
  currentPage: number
  searchParams: Required<SearchParams>
  opportunityLoading: boolean
  handleSearchParams: ({ name, value }: { name: string; value: string }) => void
  handleProjectClick: (projectSlug: string, id: number) => void
  handleCurrentPage: (currentPage: number) => void
  resetSearchParams: () => void
  assetClasses: { text: string; value: string }[]
  pagination: {
    total: number
    limit: number
    count: number
    offset: number
  }
  opportunityState: string
  setOpportunityState: React.Dispatch<React.SetStateAction<'Live' | 'Closed'>>
}

export interface OpportunityDetailPresenterProps {
  project: OpportunityDetails
  investHandleClick: (projectSlug: string, id: number) => void
  indicateInterestHandleClick: () => void
  isIndicatingInterest: boolean
  backToProjectHandler: () => void
  userInvestmentLatest?: Investment
  showContactusModal: boolean
  handleClickModal: () => void
  investButtonProperty: {
    buttonText: string
    isDisable: boolean
    kycStatus: (typeof kycStatusMap)[number]
    investmentInfo: JSX.Element
  }
  kycStatus: (typeof kycStatusMap)[number]
  opportunityDetails: CMSOpportunityDetailsType
}

export interface OpportunityDetailTopProps {
  project: OpportunityDetails
  opportunityDetails: CMSOpportunityDetailsType
  investHandleClick: (projectSlug: string, id: number) => void
  indicateInterestHandleClick: () => void
  isIndicatingInterest: boolean
  backToProjectHandler: () => void
  userInvestmentLatest?: Investment
  investButtonProperty?: {
    buttonText: string
    isDisable: boolean
    kycStatus: (typeof kycStatusMap)[number]
    investmentInfo: JSX.Element
  }
  kycStatus: (typeof kycStatusMap)[number]
}

export interface OpportunityDetailHighlightsProps {
  project: OpportunityDetails
  opportunityDetails?: CMSOpportunityDetailsType
}

export interface OpportunityInvestmentProjectionProps {
  project: OpportunityDetails
  expectedReturn?: string | null
}

export interface OpportunityEstimatedFinancialsProps {
  project: OpportunityDetails
  opportunityDetails?: CMSOpportunityDetailsType
  onMissingDataClick?: () => void
}

export type EstimatedFinancialComparisonSeries = {
  metricName: string
  label: string
  currencyCode: string
  barColor: string
}

export interface OpportunityEstimatedFinancialsChartProps {
  graphType: EstimatedFinancialGraphType
  data: EstimatedFinancialChartPoint[]
  metricName: string
  currencyCode: string
  barColor: string
  label: string
  comparisonSeries?: EstimatedFinancialComparisonSeries
}

export type EstimatedFinancialGraphType = 'bar' | 'line' | 'bar+bar' | 'bar+line' | 'line+line'

export type EstimatedFinancialChartPoint = {
  year: string
  value: number | null
  secondaryValue?: number | null
}

export type EstimatedFinancialChartMetricSeries = {
  metricName: string
  label: string
  dataKey: string
  currencyCode: string
  barColor: string
  isPercent: boolean
}

export type EstimatedFinancialCombinedChartPoint = {
  year: string
  [metricDataKey: string]: string | number | null
}

export type EstimatedFinancialTableRow = {
  metricName: string
  label: string
  valuesByYear: Record<string, string>
}

export interface OpportunityNewsProps {
  opportunityDetails?: CMSOpportunityDetailsType
}

export type ProjectionTooltipPoint = {
  label: string
  monthOffset: number
  value: number
}

export interface OpportunityInvestmentProjectionInputProps {
  currencyCode?: string
  ticketSize?: string
  sliderMin: number
  sliderMax: number
  sliderStep: number
  startingAmount: number
  onAmountChange: (event: ChangeEvent<HTMLInputElement>) => void
}

export interface OpportunityInvestmentProjectionSummaryProps {
  expectedReturnLabel: string
  selectedRangeLabel: string
  potentialTotalValueLabel: string
}

export interface OpportunityInvestmentProjectionChartProps {
  currencyCode?: string
  filteredProjectionSeries: ProjectionTooltipPoint[]
  hasProjection: boolean
  onRangeChange: (rangeValue: string) => void
  selectedProjectedIncomeLabel: string
  selectedProjectionValueLabel: string
  selectedRange: string
  selectedRangeLabel: string
  startingAmount: number
}

export type ProjectionTooltipProps = {
  active?: boolean
  payload?: ReadonlyArray<{ payload?: ProjectionTooltipPoint }>
  currencyCode?: string
  startingAmount: number
}

export interface OpportunityCompanyDetailProps {
  project: OpportunityDetails
  investHandleClick: (projectSlug: string, id: number) => void
  userInvestmentLatest?: Investment
  investButtonProperty?: {
    buttonText: string
    isDisable: boolean
    kycStatus: (typeof kycStatusMap)[number]
    investmentInfo: JSX.Element
  }
}

// invest

/** How accrued interest on fixed income should be handled (stored on investment metadata). */
export type InterestIncomePreference = 'payout' | 'reinvestment'

export interface UserInvestmentDetails {
  canInvest: string
  investmentLimit: string
  kycStatus: string
  totalInvestment: string
  walletAvailable?: number | string
  pendingTopup?: number | string
  canIntroduce?: number | string
}

export interface Investment {
  id: number
  created_at: string
  updated_at: string
  investor_id: number
  opportunity_id: number
  amount: string
  /** Share-priced deals: whole shares and cents amounts stored on the order. */
  shares?: number | null
  price_per_share?: string | null
  sales_fee?: string | null
  final_amount?: string | null
  status: InvestmentStatusType_v1
  metadata: {
    completedSteps: number
    paymentCopy?: string
    transactionNumber?: string
    bankDetailsConfirmed?: boolean
    interestIncomePreference?: InterestIncomePreference
    payFromWallet?: boolean
    walletDebited?: boolean
  }
  buy_date: string | null
  quantity: number | null
  unit_buy_price: number | null
  remaining_quantity: number | null
  platform_fee: {
    amount: number
    currency: string
  } | null
  order_id: string
  unit_allocation_date: string | null
}

export interface UpdateInvestmentPayload {
  investmentId: number
  userId: number
  projectId: number
  metadata: {
    completedSteps: number
    agreement?: string
    bankDetailsConfirmed?: boolean
    amountStepCancelled?: boolean
    interestIncomePreference?: InterestIncomePreference
    payFromWallet?: boolean
    walletDebited?: boolean
  }
}

export interface InvestmentEssentialsFormModalProps {
  active: boolean
  handleOnClose(): void
  opportunity: OpportunityDetails
}

export interface InvestmentDocument {
  created_at: string
  updated_at: string
  investor_id: number
  status: DocumentStatus
  document_type: DocumentType
  document_url: string
  metadata: any
  document_id: string
}

export type CompletePaymentHandler = (swiftPaymentCopy: string, transactionNumber: string) => void

export type PaymentFileUploadHandler = (
  fileData: File,
  purpose?: FileUploadPurpose,
) => Promise<{ data: FileUpload; status: number } | undefined>

export interface InvestPresenterProps {
  backToProjectHandler: () => void
  currentStep: number
  handleCurrentStep: (val: number) => void
  opportunity: OpportunityDetails
  userInvestmentDetail: UserInvestmentDetails
  investmentValue: number
  investmentValueForTermsheet: number
  setInvestmentValue: React.Dispatch<React.SetStateAction<number>>
  investmentData: Investment
  handleDisableButton: () => boolean
  handleInvestmentAmount: () => void
  handleFileUpload: PaymentFileUploadHandler
  handleCompletePayment: CompletePaymentHandler
  navigateToPaymentSuccessScreen: () => void
  handleAgreementandBankDetailsConfirmation: () => void
  completedStep: number
  setIsApiError: React.Dispatch<React.SetStateAction<boolean>>
  showContactUsModal: boolean
  handleShowContactUsModal: () => void
  handleShowExtendLimitModal: () => void
  hasPendingLimitRequest?: boolean
  showInvestmentEssentialsModal: boolean
  setInvestmentEssentialsModal: React.Dispatch<React.SetStateAction<boolean>>
  isTransactionChecklistChecked: boolean
  setIsTransactionChecklistChecked: React.Dispatch<React.SetStateAction<boolean>>
  showTransactionChecklistModal: boolean
  setTransactionChecklistModal: React.Dispatch<React.SetStateAction<boolean>>
  isTermsheetAgreementChecked: boolean
  setIsTermsheetAgreementChecked: React.Dispatch<React.SetStateAction<boolean>>
  showTermsheetAgreementModal: boolean
  setTermsheetAgreementModal: React.Dispatch<React.SetStateAction<boolean>>
  isTermsheetAgreementNeeded: boolean
  acceptTOBCheckbox: boolean
  setAcceptTOBCheckbox: React.Dispatch<React.SetStateAction<boolean>>
  showPopupBlockedModal: boolean
  isTransitioning: boolean
}

export interface InvestmentAmountProps {
  project: OpportunityDetails
  userInvestmentDetail: UserInvestmentDetails
  investmentValue: number
  setInvestmentValue: React.Dispatch<React.SetStateAction<number>>
  handleShowExtendLimitModal: () => void
  hasPendingLimitRequest?: boolean
  handleDisableButton: () => boolean
  handleInvestmentAmount: () => void
  setIsApiError: React.Dispatch<React.SetStateAction<boolean>>
  investmentData: any
  backToProjectHandler: () => void
}

export interface LegalAgreementProps {
  acceptTOBCheckbox: boolean
  setAcceptTOBCheckbox: React.Dispatch<React.SetStateAction<boolean>>
  opportunity: OpportunityDetails
  /** When set, replaces the default H4 guidance text (e.g. invest-v2 copy) */
  pageHeaderText?: string
  /** invest-v2: TOB can be unchecked */
  variant?: 'default' | 'invest-v2'
  /** Disable TOB checkbox while a request is in flight (e.g. invest-v2) */
  isSubmitting?: boolean
}

// payment

export interface PaymentPresenterProps {
  handlePrintPaymentSummary: () => void
  opportunity: OpportunityDetails
  paymentPage: string
  handleViewSummary: () => void
  investmentData: Investment
  showContactUsModal: boolean
  handleShowContactUsModal: () => void
  handleFilePreview: (fileKey: string) => Promise<string>
}

export interface PaymentSummaryProps {
  handlePrintPaymentSummary: () => void
  opportunity: OpportunityDetails
  investmentData: any
  handleShowContactUsModal: () => void
  handleFilePreview: (fileKey: string) => Promise<string>
}

export interface PaymentUpload {
  opportunity: OpportunityDetails
  investment?: Investment
  handleFileUpload?: PaymentFileUploadHandler
  handleCompletePayment?: CompletePaymentHandler
  /** When set, hides the default transfer heading (e.g. invest-v2 embed). */
  hideTransferHeading?: boolean
  /** Optional secondary action (e.g. defer funding). */
  onFundLater?: () => void
  /** Override primary submit label (e.g. invest-v2 wording). */
  completePaymentLabel?: string
  /** Renders in the same row as fund actions (e.g. invest-v2 wizard back). */
  onBack?: () => void
}

// kyc

export interface KYCDetails {
  created_at: string
  updated_at: string
  investor_id: number
  is_nda_accepted: boolean
  status: string
  metadata: any
  investor_kyc_id: string
  investment_essentials_agreement: boolean
  accept_tob: boolean
  investor_portfolio_id: string
  transaction_checklist_agreement: boolean
  self_declaration: boolean
  client_agreement_signed: boolean
  client_agreement_service_type?: 'check1' | 'check2' | 'check3' | null
}

export interface KYCPresenterProps {
  getKYC: () => Promise<void>
  handleClickModal: () => void
  handleSelfDeclarationSigned: () => void
  // REMOVED: hideSelfDeclarationSigningComponent - now clubbed with client agreement
  handleClientAgreementSigned: () => void
  hideClientAgreementSigningComponent: () => void
  noOfRejectedKyc: number
  // REMOVED: showSelfDeclarationSigning - now clubbed with client agreement
  showClientAgreementSigning: boolean
  accountStatus: string
  MAX_RETRY: number
  showContactUsModal: boolean
  profileTab: string | undefined
  kycStatusData: string | undefined
  portfolioIds: Array<{ investor_portfolio_id: string; status: string; created_at: string }>
  selfDeclarationSigned: boolean
  clientAgreementSigned: boolean
}

export interface InvestorBankDetails {
  accountNumber: string
  bankAddress: string
  beneficiaryBank: string
  beneficiaryName: string
  cif: string
  swiftCode: string
}

export interface KYCDocumentsChecklist {
  isEmailVerified: boolean
  isNdaAccepted: boolean
  isInvestmentEssentialsAgreementAccepted: boolean
  isTobAccepted: boolean
  isTransactionChecklistAgreementAccepted: boolean
  clientAgreementDocumentId: null | string
  welcomeLetter: null | string
  selfDeclaration: boolean
  kycStatus: string
  kycDocuments: KYCUploadedDocument[]
}

export interface KYCUploadedDocument {
  name: string
  documentData: {
    id: string
    addedDate: string
    fileMetadata: {
      fileName: string
      fileType: string
    }
  } | null
}

export interface KYCDocumentsPresenterProps {
  kycDocumentsChecklist: KYCDocumentsChecklist
  handleWelcomeLetterDownload: (welcomeLetterLink: string) => void
  handleClientAgreementDownload: (clientAgreementLink: string) => void
  handleSumsubKYCDocumentDownload: (sumsubDocumentData: KYCUploadedDocument | null) => void
}

// file

export interface FileUpload {
  ETag: string
  ServerSideEncryption: string
  Location: string
  key: string
  Key: string
  Bucket: string
  url: string
}

// withdraw

export interface WithdrawPresenterProps {
  backToInvestmentHandler: () => void
  opportunity: OpportunityDetails
  currentStep: number
  investmentSummary: DashboardSummary
  withdrawalAmount: string
  minWithdrawalAmount: string
  maxWithdrawalAmount: string
  handleDisableButton: () => boolean
  setWithdrawalAmount: React.Dispatch<React.SetStateAction<string>>
  handleSubmitWithdrawalAmount: () => void
  handlePlaceWithdrawalRequest: () => Promise<boolean>
  onWithdrawalPlacedSuccess: () => void
}

export interface WithdrawalAmountProps {
  opportunity: OpportunityDetails
  investmentSummary: DashboardSummary
  withdrawalAmount: string
  minWithdrawalAmount: string
  maxWithdrawalAmount: string
  setWithdrawalAmount: React.Dispatch<React.SetStateAction<string>>
  handleDisableButton: () => boolean
  backToInvestmentHandler: () => void
  handleSubmitWithdrawalAmount: () => void
}

export interface WithdrawalApprovalDetails {
  withdrawalApprovalDate?: string
  amountReceivableDate?: string
  grossMoneyToBeReceived?: number
  charges?: {
    exitLoad: number
    profitSharing: number
  }
  netMoneyToBeReceived?: number
  withdrawalAmount?: number
  effectiveReturn?: number
}

export interface Withdrawal {
  id: number
  amount: string
  status: WithdrawalStatusType
  opportunity_id?: number
  /** Some API responses use camelCase */
  opportunityId?: number
  opportunity_title?: string
  asset_class?: AssetsClass
  metadata: {
    completedSteps: number
    bankDetailsConfirmed?: boolean
    withdrawalInitiatedDate?: string
    withdrawalOrderPlacedDate?: string
    withdrawalApprovalDate?: string
    paymentReceivedDate?: string
    tradeExecutionDate?: string
    withdrawalCancelledDate?: string
    withdrawalRejectedDate?: string
    /** ISO date string; shown as EST. badge on dashboard withdrawal cards when present */
    expectedDateOfFundsPayout?: string
  }
  platform_fee: {
    amount: number
    currency: string
  }
  approvalDetails?: WithdrawalApprovalDetails
}

export interface WithdrawalCardProps {
  projectName: string
  withdrawalStatus: string
  withdrawalAmount: string
  assetClass: string
  activeStep: number
  capitalGains: number
  dataTestId: string
  platformFee: {
    amount: number
    currency: string
  }
  metadata: Withdrawal['metadata']
  withdrawalApprovalDetails?: WithdrawalApprovalDetails
}

/** Investor allocation interest for an opportunity (`/v1/opportunity/:id/interest`) */
export interface OpportunityAllocationInterest {
  id: number
  investor_id: number
  opportunity_id: number
  /** Amount in cents */
  amount: number
  created_at: string
  updated_at: string
}

export interface OpportunityInterestFormProps {
  name: string
  email: string
  phone: string
  description: string
  pitchDeck: string | null
}

// CMS

export type SanityColor = {
  _type: 'color'
  hex: string
  alpha: number
  rgb?: {
    r: number
    g: number
    b: number
    a: number
  }
  hsl?: {
    h: number
    s: number
    l: number
    a: number
  }
  hsv?: {
    h: number
    s: number
    v: number
    a: number
  }
}

export interface CMSSectionBase {
  showSection: boolean
  /** When true on expandable layout, section starts expanded; otherwise collapsed by default (Sanity `expandSection`). */
  expandSection?: boolean
  displayOrder: number
  /** Section title from Sanity; components fall back to a default label when absent. */
  header?: string
  /** When set, card-style blocks in the section use this fill (see strengths). */
  backgroundColor?: SanityColor
}

export interface CMSOpportunityColorType {
  _type?: string
  backgroundColor?: SanityColor
}

export interface CMSOpportunityDetailsAboutLinksType {
  linkTitle: string
  linkUrl: string
}

export interface CMSEstimatedFinancialDataPoint {
  year: string
  value: number
}

export interface CMSFinancialMetric {
  metricName: string
  currencyUnits?: string | null
  graphType?: string | null
  graphColor?: SanityColor | string | null
  /** @deprecated Prefer `graphColor` from Sanity */
  metricColor?: SanityColor | string | null
  dataPoints?: CMSEstimatedFinancialDataPoint[]
}

export interface CMSOpportunityDetailsEstimatedFinancialsType {
  showSection: boolean
  displayOrder?: number
  currencyUnits?: string | null
  heading?: string | null
  disclaimer?: string | null
  tooltip?: string | null
  financialMetrics?: CMSFinancialMetric[]
}

export interface CMSFeesCustomKeyValue {
  label: string
  value: string
}

export interface CMSOpportunityDetailsFeesType {
  targetReturn?: string | null
  hurdleRate?: string | null
  carryRate?: string | null
  upfrontCommission?: string | null
  managementFee?: string | null
  customKeysValues?: CMSFeesCustomKeyValue[] | null
}

export interface CMSOpportunityDetailsNewsType {
  showSection: boolean
  displayOrder?: number
  heading?: string | null
  articles?:
    | {
        headline?: string | null
        publishedDate?: string | null
        summary?: string | null
        image?: string | null
        articleUrl?: string | null
        sources?:
          | {
              sourceName: string
              sourceUrl: string
            }[]
          | null
      }[]
    | null
}

export interface CMSOpportunityDetailsAboutType extends CMSSectionBase {
  aboutText: PortableTextBlock[]
  layoutColumns: number
  opportunityImages: string[]
}

export interface CMSOpportunityDetailsSourceCountriesType extends CMSSectionBase {
  sourceCountryText: PortableTextBlock[]
  sourceCountryImage: string
  sourceCountryImagePosition: 'left' | 'right'
}

export interface CMSOpportunityDetailsAboutFundType extends CMSSectionBase {
  aboutFundText: PortableTextBlock[]
}

export interface CMSOpportunityDetailsRegulationType extends CMSSectionBase {
  regulationText: PortableTextBlock[]
}

export interface CMSOpportunityDetailsFactSheetType extends CMSSectionBase {
  facts: {
    factTextImage: string
    factTextPrimary: string
    factTextSecondary: string
  }[]
  images: string[]
  sectionText: PortableTextBlock[]
}

export interface CMSOpportunityDetailsProcessType extends CMSSectionBase {
  points: Array<{ blockText: PortableTextBlock[] }>
}

export interface CMSOpportunityDetailsImagesType extends CMSSectionBase {
  images: string[]
}

export interface CMSOpportunityDetailsRiskManagementType extends CMSSectionBase {
  layoutColumns: number
  textItems: { blockText: PortableTextBlock[] }[]
  riskManagementImage: string
}

export interface CMSOpportunityDetailsExecutiveSummaryType extends CMSSectionBase {
  executiveSummaryText: PortableTextBlock[]
  executiveSummaryImages: string[]
  layoutColumns: number
}

export interface CMSOpportunityDetailsStrengthsType extends CMSSectionBase {
  _key?: string
  strengthItems: {
    primaryText: string
    blockText: PortableTextBlock[]
  }[]
  layoutColumns: number
  imageLayoutColumns: number
  images: string[]
  text: PortableTextBlock[]
  sectionVariant: 'sectionsWithCards' | 'card-grid'
  sectionsWithCards: {
    sectionTitle: string
    sectionVariant: 'textCardsLayout' | 'imageWithTextCard'
    layoutColumns: number
    strengthItems: {
      primaryText: string
      blockText: PortableTextBlock[]
    }[]
    image: string
    imagePosition: 'left' | 'right'
    text: PortableTextBlock[]
  }[]
}

export interface CMSOpportunityDetailsCompetitiveAdvantageType extends CMSSectionBase {
  text: PortableTextBlock[]
  image: string
  imagePosition: 'left' | 'right'
}

export interface CMSOpportunityDetailsWhyShouldYouInvestType extends CMSSectionBase {
  reasons: {
    primaryText: string
    blockText: PortableTextBlock[]
    image: string
    imagePosition: 'left' | 'right'
  }[]
}
export interface CMSOpportunityDetailsKeyPartnerShipsType extends CMSSectionBase {
  partnerShips: {
    partnerImage: string
    text?: PortableTextBlock[]
  }[]
  sectionVariant: 'detailed' | 'gallery'
  layoutColumns: number
  partnerShipText: PortableTextBlock[]
}

export interface CMSOpportunityDetailsLeadershipTeamType extends CMSSectionBase {
  sectionTitle: string
  text: PortableTextBlock[]
  sectionPosition: 'top' | 'bottom'
  teamMembers: {
    image: string
    name: string
    designation: string
    about: PortableTextBlock[]
    linkedInUrl: string
  }[]
}

export interface CMSOpportunityDetailsRiskInvolvedType extends CMSSectionBase {
  text: PortableTextBlock[]
}

export interface CMSOpportunityDetailsAdditionalSectionsType extends CMSSectionBase {
  sections: {
    showSection: boolean
    expandSection?: boolean
    sectionTitle: string
    backgroundColor: SanityColor
    image: string
    imagePosition: 'left' | 'right'
    sectionText: PortableTextBlock[]
    sectionVariant: 'variant-1' | 'variant-2'
    images: string[]
  }[]
  text: PortableTextBlock[]
}

export interface CMSOpportunityDetailsClientBaseType extends CMSSectionBase {
  clients: string[]
  sectionTitle: string
}

export interface CMSOpportunityDetailsOpportunityVideosType extends CMSSectionBase {
  videos?: string[]
}

export interface CMSOpportunityDetailsType {
  showInvestorBacking?: boolean
  /** When true, show Raise Goal (or Committed Value fallback) in opportunity highlights. */
  showRaiseGoal?: boolean
  opportunityBacking?: string
  opportunityTitle?: string
  opportunityColor?: CMSOpportunityColorType
  /** Resolved hero image URL for the detail page top backdrop. */
  heroImage?: string | null
  /** Resolved logo image URL when provided by CMS (Sanity image or plain URL). */
  logo?: string | null
  aboutLinks?: CMSOpportunityDetailsAboutLinksType[]
  estimatedFinancials?: CMSOpportunityDetailsEstimatedFinancialsType
  fees?: CMSOpportunityDetailsFeesType
  news?: CMSOpportunityDetailsNewsType
  about?: CMSOpportunityDetailsAboutType
  sourceCountries?: CMSOpportunityDetailsSourceCountriesType
  aboutFund?: CMSOpportunityDetailsAboutFundType
  regulation?: CMSOpportunityDetailsRegulationType
  factSheet?: CMSOpportunityDetailsFactSheetType
  process?: CMSOpportunityDetailsProcessType
  opportunityImages?: CMSOpportunityDetailsImagesType
  riskManagement?: CMSOpportunityDetailsRiskManagementType
  executiveSummary?: CMSOpportunityDetailsExecutiveSummaryType
  /** @deprecated Prefer `strengthSections` — kept for legacy CMS documents */
  strengths?: CMSOpportunityDetailsStrengthsType
  strengthSections?: CMSOpportunityDetailsStrengthsType[]
  competitiveAdvantage?: CMSOpportunityDetailsCompetitiveAdvantageType
  whyShouldYouInvest?: CMSOpportunityDetailsWhyShouldYouInvestType
  keyPartnerships?: CMSOpportunityDetailsKeyPartnerShipsType
  leadershipTeam?: CMSOpportunityDetailsLeadershipTeamType
  risksInvolved?: CMSOpportunityDetailsRiskInvolvedType
  additionalSections?: CMSOpportunityDetailsAdditionalSectionsType
  clientBase?: CMSOpportunityDetailsClientBaseType
  opportunityVideos?: CMSOpportunityDetailsOpportunityVideosType
}
